/**
 * Document-local identifiers.
 *
 * ⚠️ `crypto.randomUUID` IS NOT USED, AND NOT BECAUSE OF SUPPORT. These ids are written into a
 * document that a human reads while debugging and that a model emits while patching. `blk_k3f9a2`
 * is quotable in a chat message and in a patch path; `blk_0b7a1e4c-…` is not, and a model asked to
 * reference one gets a character wrong often enough to matter.
 *
 * Collision domain is ONE DOCUMENT, not the world. 36^8 within a site of a few hundred blocks is far
 * past the point where the birthday bound stops being interesting — and uniqueness is checked by
 * `validateSite` regardless, because an id collision from a bad patch must be *reported*, not
 * probabilistically avoided.
 */

const ALPHABET = 'abcdefghijklmnopqrstuvwxyz0123456789'

/** Random bytes, from whichever runtime this is in. `globalThis.crypto` exists in browsers, in Node
 *  ≥19 and in Workers — which is exactly the three places this package runs. */
function randomBytes(n: number): Uint8Array {
  const out = new Uint8Array(n)
  globalThis.crypto.getRandomValues(out)
  return out
}

export function newId(prefix: string): string {
  const bytes = randomBytes(8)
  let s = ''
  for (const b of bytes) s += ALPHABET[b % ALPHABET.length]
  return `${prefix}_${s}`
}

/** Every id in a document must be unique within it, and must look like one. Used by `validateSite`
 *  and by the AI patch applier, which refuses a patch that mints an id already in use. */
export function isId(value: unknown): value is string {
  return typeof value === 'string' && /^[a-z]+_[a-z0-9]{4,32}$/.test(value)
}
